import React, { useMemo, useState } from 'react';

import JsonView from '@uiw/react-json-view';
import { Collapse, Modal, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDateTime } from '@/shared/lib/format/dateTime';
import { Icon } from '@/shared/ui/Icon';
import { Switch } from '@/shared/ui/Switch';

import type { RunListItem } from '@/entities/run/domain/runManagementTypes';
import { useRunComparison } from '@/entities/run/model/useRunComparison';

import styles from './RunComparisonModal.module.css';

type RunComparisonModalProps = {
  open: boolean;
  runs: RunListItem[];
  onClose: () => void;
};

type ComparisonRow = {
  key: string;
  name: string;
  isDifferent: boolean;
  values: (string | number | Record<string, unknown>)[];
};

const isJsonObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const ValueCell: React.FC<{ value: string | number | Record<string, unknown> }> = ({ value }) => {
  if (value === '-' || value === '' || value === null || value === undefined) {
    return <span>-</span>;
  }

  if (isJsonObject(value)) {
    return (
      <div className={styles.jsonViewWrapper}>
        <JsonView
          value={value}
          collapsed={1}
          displayDataTypes={false}
          displayObjectSize={false}
          enableClipboard={false}
          style={
            {
              '--w-rjv-font-family': "'Monaco', 'Menlo', 'Consolas', monospace",
              '--w-rjv-font-size': 'var(--opds-glo-font-size-11)',
              '--w-rjv-line-height': '1.5',
              '--w-rjv-background-color': 'var(--opds-glo-palette-gray-98)',
              '--w-rjv-border-radius': 'var(--opds-sem-round-xsmall)',
              '--w-rjv-color': 'var(--opds-sem-color-onSurface-standard-level1)',
              '--w-rjv-key-string': 'var(--opds-sem-color-primary-level1)',
              '--w-rjv-quotes-string-color': 'var(--opds-glo-palette-green-50)',
              '--w-rjv-info-color': 'var(--opds-glo-palette-gray-60)',
              '--w-rjv-number-color': 'var(--opds-glo-palette-blue-50)',
              '--w-rjv-boolean-color': 'var(--opds-glo-palette-purple-50)',
              '--w-rjv-null-color': 'var(--opds-glo-palette-gray-50)',
            } as React.CSSProperties
          }
        />
      </div>
    );
  }

  return <span>{String(value)}</span>;
};

export const RunComparisonModal: React.FC<RunComparisonModalProps> = ({ open, runs, onClose }) => {
  if (!open) {
    return null;
  }

  return <RunComparisonModalContent runIds={runs.map((run) => run.id)} onClose={onClose} />;
};

const RunComparisonModalContent: React.FC<{ runIds: number[]; onClose: () => void }> = ({ runIds, onClose }) => {
  const { data, isLoading } = useRunComparison(runIds);
  const [showDiffOnly, setShowDiffOnly] = useState(false);

  const columns: ColumnsType<ComparisonRow> = useMemo(() => {
    const baseColumns: ColumnsType<ComparisonRow> = [
      {
        title: '',
        dataIndex: 'name',
        key: 'name',
        width: 250,
        fixed: 'left',
        className: styles.nameColumn,
      },
    ];

    if (data?.runs) {
      data.runs.forEach((runData, index) => {
        baseColumns.push({
          title: runData.run?.runName || `Run ${runIds[index]}`,
          key: String(runIds[index]),
          render: (_, record) => <ValueCell value={record.values[index] || '-'} />,
        });
      });
    }

    return baseColumns;
  }, [runIds, data]);

  const runDetailsRows: ComparisonRow[] = useMemo(() => {
    if (!data?.runs || data.runs.length === 0) return [];

    const rows: ComparisonRow[] = [];

    const runIdValues = data.runs.map((d) => d.run?.runId || '-');
    const nameValues = data.runs.map((d) => d.run?.runName || '-');
    const statusValues = data.runs.map((d) => d.run?.status || '-');
    const startTimeValues = data.runs.map((d) => (d.run?.startTime ? formatDateTime(d.run.startTime) : '-'));
    const endTimeValues = data.runs.map((d) => (d.run?.endTime ? formatDateTime(d.run.endTime) : '-'));
    const creatorValues = data.runs.map((d) => d.creator || '-');
    const createdAtValues = data.runs.map((d) => (d.createdAt ? formatDateTime(d.createdAt) : '-'));
    const updatedAtValues = data.runs.map((d) => (d.updatedAt ? formatDateTime(d.updatedAt) : '-'));
    const descriptionValues = data.runs.map((d) => d.run?.description || d.description || '-');

    rows.push(
      {
        key: 'runId',
        name: 'Run ID:',
        values: runIdValues,
        isDifferent: new Set(runIdValues).size > 1,
      },
      {
        key: 'name',
        name: 'Run Name:',
        values: nameValues,
        isDifferent: new Set(nameValues).size > 1,
      },
      {
        key: 'status',
        name: 'Status:',
        values: statusValues,
        isDifferent: new Set(statusValues).size > 1,
      },
      {
        key: 'startTime',
        name: 'Start Time:',
        values: startTimeValues,
        isDifferent: new Set(startTimeValues).size > 1,
      },
      {
        key: 'endTime',
        name: 'End Time:',
        values: endTimeValues,
        isDifferent: new Set(endTimeValues).size > 1,
      },
      {
        key: 'creator',
        name: 'Creator:',
        values: creatorValues,
        isDifferent: new Set(creatorValues).size > 1,
      },
      {
        key: 'createdAt',
        name: 'Created At:',
        values: createdAtValues,
        isDifferent: new Set(createdAtValues).size > 1,
      },
      {
        key: 'updatedAt',
        name: 'Updated At:',
        values: updatedAtValues,
        isDifferent: new Set(updatedAtValues).size > 1,
      },
      {
        key: 'description',
        name: 'Description:',
        values: descriptionValues,
        isDifferent: new Set(descriptionValues).size > 1,
      }
    );

    return rows;
  }, [data]);

  const datasetRows: ComparisonRow[] = useMemo(() => {
    if (!data?.runs || data.runs.length === 0) return [];

    const rows: ComparisonRow[] = [];

    const snapshotIdValues = data.runs.map((d) => d.dataset?.snapshotId || '-');
    const tierValues = data.runs.map((d) => d.dataset?.tier || '-');
    const namespaceValues = data.runs.map((d) => d.dataset?.namespace || '-');
    const tableValues = data.runs.map((d) => d.dataset?.table || '-');
    const rowFilterValues = data.runs.map((d) => d.dataset?.rowFilter || '-');
    const rowCountValues = data.runs.map((d) => d.dataset?.rowCount ?? '-');
    const columnCountValues = data.runs.map((d) => d.dataset?.columnCount ?? '-');

    rows.push(
      {
        key: 'dataset-snapshotId',
        name: 'Snapshot ID:',
        values: snapshotIdValues,
        isDifferent: new Set(snapshotIdValues).size > 1,
      },
      {
        key: 'dataset-tier',
        name: 'Tier:',
        values: tierValues,
        isDifferent: new Set(tierValues).size > 1,
      },
      {
        key: 'dataset-namespace',
        name: 'Namespace:',
        values: namespaceValues,
        isDifferent: new Set(namespaceValues).size > 1,
      },
      {
        key: 'dataset-table',
        name: 'Table:',
        values: tableValues,
        isDifferent: new Set(tableValues).size > 1,
      },
      {
        key: 'dataset-rowFilter',
        name: 'Row Filter:',
        values: rowFilterValues,
        isDifferent: new Set(rowFilterValues).size > 1,
      },
      {
        key: 'dataset-rowCount',
        name: 'Row Count:',
        values: rowCountValues,
        isDifferent: new Set(rowCountValues).size > 1,
      },
      {
        key: 'dataset-columnCount',
        name: 'Column Count:',
        values: columnCountValues,
        isDifferent: new Set(columnCountValues).size > 1,
      }
    );

    return rows;
  }, [data]);

  const featureSetRows: ComparisonRow[] = useMemo(() => {
    if (!data?.runs || data.runs.length === 0) return [];

    const rows: ComparisonRow[] = [];

    const idValues = data.runs.map((d) => d.featureSet?.id?.toString() || '-');
    const snapshotIdValues = data.runs.map((d) => d.featureSet?.snapshotId || '-');
    const tierValues = data.runs.map((d) => d.featureSet?.tier || '-');
    const namespaceValues = data.runs.map((d) => d.featureSet?.namespace || '-');
    const tableValues = data.runs.map((d) => d.featureSet?.table || '-');
    const rowCountValues = data.runs.map((d) => d.featureSet?.rowCount ?? '-');
    const columnCountValues = data.runs.map((d) => d.featureSet?.columnCount ?? '-');

    rows.push(
      {
        key: 'featureSet-id',
        name: 'ID:',
        values: idValues,
        isDifferent: new Set(idValues).size > 1,
      },
      {
        key: 'featureSet-snapshotId',
        name: 'Snapshot ID:',
        values: snapshotIdValues,
        isDifferent: new Set(snapshotIdValues).size > 1,
      },
      {
        key: 'featureSet-tier',
        name: 'Tier:',
        values: tierValues,
        isDifferent: new Set(tierValues).size > 1,
      },
      {
        key: 'featureSet-namespace',
        name: 'Namespace:',
        values: namespaceValues,
        isDifferent: new Set(namespaceValues).size > 1,
      },
      {
        key: 'featureSet-table',
        name: 'Table:',
        values: tableValues,
        isDifferent: new Set(tableValues).size > 1,
      },
      {
        key: 'featureSet-rowCount',
        name: 'Row Count:',
        values: rowCountValues,
        isDifferent: new Set(rowCountValues).size > 1,
      },
      {
        key: 'featureSet-columnCount',
        name: 'Column Count:',
        values: columnCountValues,
        isDifferent: new Set(columnCountValues).size > 1,
      }
    );

    return rows;
  }, [data]);

  const parameterSetRows: ComparisonRow[] = useMemo(() => {
    if (!data?.runs || data.runs.length === 0) return [];

    const allParamKeys = new Set<string>();
    data.runs.forEach((d) => {
      if (d.parameterSet?.hyperparameters) {
        Object.keys(d.parameterSet.hyperparameters).forEach((key) => allParamKeys.add(key));
      }
    });

    return Array.from(allParamKeys)
      .sort()
      .map((paramKey) => {
        const values = data.runs.map((d) => {
          const paramValue = d.parameterSet?.hyperparameters?.[paramKey];
          if (typeof paramValue === 'string' || typeof paramValue === 'number') {
            return paramValue;
          }
          if (paramValue !== null && paramValue !== undefined) {
            return paramValue as Record<string, unknown>;
          }
          return '-';
        });

        return {
          key: `param-${paramKey}`,
          name: paramKey,
          values,
          isDifferent: new Set(values.map((v) => JSON.stringify(v))).size > 1,
        };
      });
  }, [data]);

  const metricsRows: ComparisonRow[] = useMemo(() => {
    if (!data?.runs || data.runs.length === 0) return [];

    const allMetricKeys = new Set<string>();
    data.runs.forEach((d) => {
      if (d.metrics?.final) {
        Object.keys(d.metrics.final).forEach((key) => allMetricKeys.add(key));
      }
    });

    return Array.from(allMetricKeys)
      .sort()
      .map((metricKey) => {
        const values = data.runs.map((d) => {
          const metricValue = d.metrics?.final?.[metricKey];
          if (typeof metricValue === 'string' || typeof metricValue === 'number') {
            return metricValue;
          }
          if (metricValue !== null && metricValue !== undefined) {
            return metricValue as Record<string, unknown>;
          }
          return '-';
        });

        return {
          key: `metric-${metricKey}`,
          name: metricKey,
          values,
          isDifferent: new Set(values.map((v) => (typeof v === 'number' ? v.toFixed(4) : JSON.stringify(v)))).size > 1,
        };
      });
  }, [data]);

  const filterRowsByDiff = (rows: ComparisonRow[]) => {
    return showDiffOnly ? rows.filter((row) => row.isDifferent) : rows;
  };

  const getRowClassName = (record: ComparisonRow) => {
    return record.isDifferent ? styles.differentRow : '';
  };

  const collapseItems = [
    {
      key: 'run_details',
      label: 'Run 정보',
      children: (
        <Table
          dataSource={filterRowsByDiff(runDetailsRows)}
          columns={columns}
          pagination={false}
          size="small"
          className={styles.comparisonTable}
          rowClassName={getRowClassName}
        />
      ),
    },
    {
      key: 'metrics',
      label: '메트릭',
      children: (
        <Table
          dataSource={filterRowsByDiff(metricsRows)}
          columns={columns}
          pagination={false}
          size="small"
          className={styles.comparisonTable}
          rowClassName={getRowClassName}
        />
      ),
    },
    {
      key: 'dataset',
      label: '데이터셋',
      children: (
        <Table
          dataSource={filterRowsByDiff(datasetRows)}
          columns={columns}
          pagination={false}
          size="small"
          className={styles.comparisonTable}
          rowClassName={getRowClassName}
        />
      ),
    },
    {
      key: 'featureSet',
      label: '피처셋',
      children: (
        <Table
          dataSource={filterRowsByDiff(featureSetRows)}
          columns={columns}
          pagination={false}
          size="small"
          className={styles.comparisonTable}
          rowClassName={getRowClassName}
        />
      ),
    },
    {
      key: 'parameters',
      label: '파라미터셋',
      children: (
        <Table
          dataSource={filterRowsByDiff(parameterSetRows)}
          columns={columns}
          pagination={false}
          size="small"
          className={styles.comparisonTable}
          rowClassName={getRowClassName}
        />
      ),
    },
  ];

  return (
    <Modal
      title={`Run 비교 (${runIds.length}개)`}
      open={true}
      onCancel={onClose}
      width={1200}
      footer={null}
      className={styles.modal}>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <Spin size="large" />
          <p>Run 데이터를 비교하는 중...</p>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.toolbar}>
            <span className={styles.switchLabel}>차이점만 보기</span>
            <Switch checked={showDiffOnly} onChange={setShowDiffOnly} aria-label="다른 값만 보기" color="standard" />
          </div>
          <Collapse
            items={collapseItems}
            defaultActiveKey={['run_details', 'metrics', 'dataset', 'featureSet', 'parameters']}
            className={styles.collapseSection}
            ghost
            expandIconPosition="end"
            expandIcon={({ isActive }) => <Icon name={isActive ? 'arrow-short-down' : 'arrow-short-up'} size={16} />}
          />
        </div>
      )}
    </Modal>
  );
};
